/**
 * Impersonation engine — the pure CORE (no React).
 *
 * Everything here is framework-free so the GraphQL client + analytics can import
 * it without dragging in the "use client" provider. It owns:
 *   - persistence: the ONE sessionStorage key (read/write/clear)
 *   - state transitions: pushTarget / popTarget / refineTopUserId (pure reducers)
 *   - derivations: deriveImpersonationView (top-of-stack "current identity") + snapshot
 *   - the network identity: getImpersonationHeaders / withImpersonationKey (whole chain)
 *
 * The ImpersonationProvider is just the React shell that calls these + does the
 * side-effects (routing, theme, query cancellation). See the module map in index.ts.
 *
 * All functions are SSR-safe — they no-op / return empty when `window` is absent.
 * See docs/impersonation.md.
 */
import { ROLE_CHOICES, RoleType } from "@/shared/lib/types";
import { ROLE_REGISTRY } from "./registry";
import type {
  ImpersonationKind,
  ImpersonationLayer,
  ImpersonationState,
  ImpersonationTarget,
} from "./types";

/** The one and only sessionStorage key for impersonation. */
const KEY = "give:impersonation";

/** Read + parse the persisted state. Returns `null` when absent, on SSR, or if corrupt. */
export const readState = (): ImpersonationState => {
  if (typeof window === "undefined") return null;
  const raw = window.sessionStorage.getItem(KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    // Corrupt value — treat as "not impersonating" rather than throwing.
    return null;
  }
};

/** Persist the state. Passing `null` clears the slot. */
export const writeState = (state: ImpersonationState): void => {
  if (typeof window === "undefined") return;
  if (state === null) {
    window.sessionStorage.removeItem(KEY);
    return;
  }
  window.sessionStorage.setItem(KEY, JSON.stringify(state));
};

/** Delete the slot entirely. */
export const clearState = (): void => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(KEY);
};

/**
 * Where the CURRENT impersonation session was launched from — persisted in its OWN slot,
 * separate from `KEY`, so it survives a full exit (which clears `KEY`). After the stack
 * empties, a browser Back into a now-context-less route can use this to send the staff
 * user back to where they started, instead of the generic `/landing`. Overwritten on each
 * fresh session start; SSR-safe like `readState`/`writeState`.
 */
const ENTRY_ORIGIN_KEY = "give:impersonation:origin";

export const writeEntryOrigin = (path: string): void => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(ENTRY_ORIGIN_KEY, path);
};

export const readEntryOrigin = (): string | null => {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(ENTRY_ORIGIN_KEY);
};

/**
 * Build the impersonation HTTP headers for the current state — called by the
 * GraphQL client on every request (in a later migration step).
 *
 * Walks the stack bottom→top, merging each layer's registry headers. Because we
 * merge in order, the TOP layer (the innermost target — who we're "acting as"
 * right now) wins for any shared header key (notably `impersonate`), while
 * intermediate layers can still contribute context keys (`family` / `firm`) the
 * backend's chain validation may want. Returns `{}` when not impersonating.
 */
export const getImpersonationHeaders = (): Record<string, string> => {
  const state = readState();
  if (!state) return {};
  return state.stack.reduce<Record<string, string>>(
    (acc, layer) => ({
      ...acc,
      // `as never` collapses the discriminated union to the exact variant the
      // registry entry for `layer.kind` expects; safe because the key and the
      // value variant always correspond by construction.
      ...ROLE_REGISTRY[layer.kind].headers(layer as never),
    }),
    {},
  );
};

/**
 * Append the current impersonation identity to a react-query key, so a query whose
 * RESULT depends on who you're impersonating (e.g. `GET_USER`, advisor lists) keeps
 * a separate cache entry per impersonated identity. The appended value is the
 * `impersonate`/`family`/`firm` headers — exactly the identity the request queries
 * as — and is `"{}"` (a constant) when not impersonating.
 *
 * IMPORTANT: any manual `setQueryData`/`getQueryData` targeting the SAME query must
 * build its key with this helper too, or it will read/write the wrong entry.
 */
export const withImpersonationKey = (
  baseKey: readonly (string | number | boolean | object)[],
): (string | number | boolean | object)[] => [
  ...baseKey,
  JSON.stringify(getImpersonationHeaders()),
];

// ── State transitions (pure reducers) ─────────────────────────────────────────
//
// The stack math, framework-free and unit-testable. The provider calls these for
// the next state and owns the side-effects (routing / theme / query cancellation).

/**
 * PUSH a layer, stamped with `enteredBy` — the role acting at this push (the
 * provider passes `resolveActor`, so a drill-down stamps the worn role). A new
 * stack records `enteredFrom`; a drill-down keeps the original.
 */
export const pushTarget = (
  state: ImpersonationState,
  target: ImpersonationTarget,
  enteredFrom: string,
  enteredBy?: ImpersonationLayer["enteredBy"],
): NonNullable<ImpersonationState> => {
  const layer: ImpersonationLayer = { ...target, enteredBy };
  return state
    ? { stack: [...state.stack, layer], enteredFrom: state.enteredFrom }
    : { stack: [layer], enteredFrom };
};

/** POP one layer. `next` is `null` once the stack empties; `newTop` is the layer now active. */
export const popTarget = (
  state: NonNullable<ImpersonationState>,
): { next: ImpersonationState; newTop: ImpersonationLayer | null } => {
  const remaining = state.stack.slice(0, -1);
  if (remaining.length === 0) return { next: null, newTop: null };
  return {
    next: {
      stack: remaining as [ImpersonationLayer, ...ImpersonationLayer[]],
      enteredFrom: state.enteredFrom,
    },
    newTop: remaining[remaining.length - 1],
  };
};

/** Fill in the active (top) target's `userId` in place (deferred-id completion). */
export const refineTopUserId = (
  state: ImpersonationState,
  userId: number,
): ImpersonationState => {
  if (!state) return state;
  const lastIndex = state.stack.length - 1;
  if (state.stack[lastIndex].userId === userId) return state; // unchanged
  const updatedTop = {
    ...state.stack[lastIndex],
    userId,
  } as ImpersonationLayer;
  return {
    stack: state.stack.map((f, i) => (i === lastIndex ? updatedTop : f)) as [
      ImpersonationLayer,
      ...ImpersonationLayer[],
    ],
    enteredFrom: state.enteredFrom,
  };
};

// ── Derivation (the "current identity" view) ──────────────────────────────────

/** Kinds that represent "acting as a client/account" (the legacy "client impersonation" group). */
const CLIENT_KINDS: ImpersonationKind[] = [
  ROLE_CHOICES[RoleType.HeadOfTheFamily],
  ROLE_CHOICES[RoleType.FamilyMember],
  ROLE_CHOICES[RoleType.CorporateAdmin],
  ROLE_CHOICES[RoleType.SelfDirected],
];

/**
 * The single source for every derived read consumers care about — all computed
 * from the TOP of the stack, i.e. who you're acting as RIGHT NOW. This is what
 * makes the booleans unambiguous under drill-down: in `Reviewer→Advisor→Client`
 * the top is the client, so `isClientImpersonation` is true and
 * `isAdvisorImpersonation` is false (you're not *currently* the advisor — the
 * advisor is just a lower layer). The whole-chain identity is a separate concern,
 * owned exclusively by `getImpersonationHeaders` / `withImpersonationKey`.
 *
 * Used by both the hook (reactive, via `state`) and `getImpersonationSnapshot`
 * (point-in-time, via `readState()`) — one definition, one vocabulary.
 */
export const deriveImpersonationView = (
  state: ImpersonationState,
): {
  current: ImpersonationTarget | null;
  isImpersonating: boolean;
  isClientImpersonation: boolean;
  isAdvisorImpersonation: boolean;
  isSelfDirectedImpersonation: boolean;
  isCorporateAdminImpersonation: boolean;
  impersonatingFamilyId: number | null;
  impersonatingClientId: number | null;
  impersonatingAdvisorId: number | null;
} => {
  const current = state ? state.stack[state.stack.length - 1] : null;
  const kind = current?.kind ?? null;
  const isClientKind = !!kind && CLIENT_KINDS.includes(kind);
  return {
    current,
    isImpersonating: current !== null,
    isClientImpersonation: isClientKind,
    isAdvisorImpersonation: kind === ROLE_CHOICES[RoleType.FinancialAdvisor],
    isSelfDirectedImpersonation: kind === ROLE_CHOICES[RoleType.SelfDirected],
    isCorporateAdminImpersonation:
      kind === ROLE_CHOICES[RoleType.CorporateAdmin],
    impersonatingFamilyId:
      current && "familyId" in current ? current.familyId : null,
    impersonatingClientId: isClientKind ? current!.userId || null : null,
    impersonatingAdvisorId:
      kind === ROLE_CHOICES[RoleType.FinancialAdvisor]
        ? current!.userId || null
        : null,
  };
};

/**
 * Point-in-time snapshot for NON-React callers (analytics, mutation/cache
 * callbacks) — reads sessionStorage once and returns the same shape the hook
 * exposes. React code reads these reactively off `useImpersonation()` instead.
 */
export const getImpersonationSnapshot = (): ReturnType<
  typeof deriveImpersonationView
> => deriveImpersonationView(readState());
